/**
 * The vendor's pipeline in one organization (docs/MarketplaceDesign.md
 * section 3.5, docs/Decisions.md D68 items 1 and 3; M2): counts and rates,
 * the salespeople and brokers named on the listing, and each lead with its
 * contact details (while the member's consent stands) and the tracker
 * actions. Marking a lead won records the deal value and the salesperson,
 * which decide its lead fee from M4 on.
 */
import { useCallback, useEffect, useState } from 'react';
import * as marketApi from '../api/marketplace';
import { useToast } from './toast';
import LeadPill from './LeadPill';
import RewardProgramEditor from './RewardProgramEditor';
import { Spinner, ErrorAlert, Field, SelectField, formatDate } from './ui';
import {
  LEAD_ACTION_LABELS,
  LOST_REASONS,
  LOST_REASON_LABELS,
  SALESPEOPLE_MAX,
  amountToCents,
  feeLabel,
  formatUsd,
  leadsToCsv,
} from '../lib/marketplace';
import { fieldErrorsFrom } from '../lib/formErrors';
import { cn, ui } from '../lib/ui';

function SalespeopleEditor({ listingId, initial, onSaved }) {
  const toast = useToast();
  const [rows, setRows] = useState(initial);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const update = (i, key, value) =>
    setRows(prev => prev.map((r, j) => (j === i ? { ...r, [key]: value } : r)));

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const data = await marketApi.setSalespeople(listingId, rows);
      toast.success('Salespeople saved');
      onSaved(data.salespeople);
    } catch (err) {
      setError(fieldErrorsFrom(err).salespeople || err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <fieldset className={cn(ui.cardFlat, 'mb-4')}>
      <legend className={ui.label}>Salespeople and brokers</legend>
      <p className={cn(ui.hint, 'mt-0')}>
        The person you assign to a won deal receives the balance of its lead fee. Tick the entry
        that is your own business: deals you sell yourself are not charged the balance.
      </p>
      {rows.map((r, i) => (
        <div key={i} className="grid gap-x-3 md:grid-cols-[2fr_2fr_auto_auto] md:items-end">
          <Field label="Name" value={r.name} onChange={e => update(i, 'name', e.target.value)} />
          <Field
            label="Email"
            type="email"
            value={r.email || ''}
            onChange={e => update(i, 'email', e.target.value)}
          />
          <label className="mb-4 flex items-center gap-2 text-body-s">
            <input
              type="checkbox"
              className={ui.checkbox}
              checked={Boolean(r.self)}
              onChange={e => update(i, 'self', e.target.checked)}
            />
            Our business
          </label>
          <button
            type="button"
            className={cn(ui.btnGhost, ui.btnSm, 'mb-4')}
            onClick={() => setRows(prev => prev.filter((_, j) => j !== i))}
          >
            Remove
          </button>
        </div>
      ))}
      {error && <p className={ui.fieldError}>{error}</p>}
      <div className={ui.row}>
        <button
          type="button"
          className={cn(ui.btnSecondary, ui.btnSm)}
          disabled={rows.length >= SALESPEOPLE_MAX}
          onClick={() => setRows(prev => [...prev, { name: '', email: '', self: false }])}
        >
          Add a person
        </button>
        <button
          type="button"
          className={cn(ui.btnPrimary, ui.btnSm)}
          disabled={saving}
          onClick={save}
        >
          {saving ? 'Saving...' : 'Save salespeople'}
        </button>
      </div>
    </fieldset>
  );
}

function LeadRow({ lead, salespeople, onChanged }) {
  const toast = useToast();
  const [mode, setMode] = useState(null);
  const [value, setValue] = useState('');
  const [salespersonId, setSalespersonId] = useState(lead.salespersonId || '');
  const [reason, setReason] = useState('no-response');
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  async function run(action, extra = {}) {
    setBusy(true);
    setErrors({});
    try {
      const data = await marketApi.actOnLead(lead.leadId, action, extra);
      toast.success(`Lead: ${LEAD_ACTION_LABELS[action].toLowerCase()}`);
      setMode(null);
      onChanged(data.lead);
    } catch (err) {
      setErrors(fieldErrorsFrom(err));
      toast.error(err.message || 'Nothing changed');
    } finally {
      setBusy(false);
    }
  }

  function win(e) {
    e.preventDefault();
    const cents = value.trim() === '' ? undefined : amountToCents(value);
    if (Number.isNaN(cents)) {
      setErrors({ dealValueCents: 'Enter an amount such as 2500 or 2500.00' });
      return;
    }
    run('win', { dealValueCents: cents, salespersonId: salespersonId || undefined });
  }

  const c = lead.contact;
  return (
    <li className="border-b border-z-line py-3">
      <div className={cn(ui.row, ui.spread)}>
        <span>
          <strong>{lead.serviceName}</strong> <LeadPill status={lead.status} />
          {lead.attributed && <span className={cn(ui.pillInfo, 'ml-2')}>Recommended</span>}
          <span className={cn(ui.hint, 'block')}>
            Joined {formatDate(lead.createdAt)}
            {lead.categoryName ? ` - ${lead.categoryName}` : ''}
            {lead.feeSnapshot?.leadFee
              ? ` - lead fee ${feeLabel(lead.feeSnapshot.leadFee, 'the deal value')}`
              : ''}
          </span>
          {c ? (
            <span className="block text-body-s">
              {c.name}
              {c.email && (
                <>
                  {' - '}
                  <a href={`mailto:${c.email}`}>{c.email}</a>
                </>
              )}
              {c.phone ? ` - ${c.phone}` : ''}
            </span>
          ) : (
            <span className={cn(ui.hint, 'block')}>Contact details withdrawn</span>
          )}
          {lead.note && <span className="block text-body-s">&ldquo;{lead.note}&rdquo;</span>}
          {lead.status === 'won' && (
            <span className="block text-body-s">
              {Number.isInteger(lead.dealValueCents)
                ? `Deal ${formatUsd(lead.dealValueCents)}`
                : 'Deal value not given'}
              {lead.salespersonName ? ` - sold by ${lead.salespersonName}` : ''}
            </span>
          )}
        </span>
        {!mode && (
          <span className={ui.row}>
            {lead.actions.includes('qualify') && (
              <button
                type="button"
                className={cn(ui.btnSecondary, ui.btnSm)}
                disabled={busy}
                onClick={() => run('qualify')}
              >
                Mark qualified
              </button>
            )}
            {lead.actions.includes('win') && (
              <button
                type="button"
                className={cn(ui.btnPrimary, ui.btnSm)}
                onClick={() => setMode('win')}
              >
                Mark won
              </button>
            )}
            {lead.actions.includes('lose') && (
              <button
                type="button"
                className={cn(ui.btnGhost, ui.btnSm)}
                onClick={() => setMode('lose')}
              >
                Mark lost
              </button>
            )}
          </span>
        )}
      </div>
      {mode === 'win' && (
        <form
          onSubmit={win}
          className={cn(ui.cardFlat, 'mt-2 grid gap-x-3 md:grid-cols-[1fr_1fr_auto] md:items-end')}
        >
          <Field
            label="Deal value (USD)"
            inputMode="decimal"
            value={value}
            onChange={e => setValue(e.target.value)}
            error={errors.dealValueCents}
            hint={
              lead.feeSnapshot?.leadFee?.type === 'percent'
                ? 'Required: the lead fee is a percentage of it.'
                : undefined
            }
          />
          <SelectField
            label="Sold by"
            value={salespersonId}
            onChange={e => setSalespersonId(e.target.value)}
            error={errors.salespersonId}
          >
            <option value="">Nobody named</option>
            {salespeople.map(s => (
              <option key={s.salespersonId} value={s.salespersonId}>
                {s.name}
                {s.self ? ' (our business)' : ''}
              </option>
            ))}
          </SelectField>
          <span className={cn(ui.row, 'mb-4')}>
            <button type="submit" className={cn(ui.btnPrimary, ui.btnSm)} disabled={busy}>
              Save as won
            </button>
            <button
              type="button"
              className={cn(ui.btnGhost, ui.btnSm)}
              onClick={() => setMode(null)}
            >
              Cancel
            </button>
          </span>
        </form>
      )}
      {mode === 'lose' && (
        <div className={cn(ui.cardFlat, 'mt-2 grid gap-x-3 md:grid-cols-[1fr_auto] md:items-end')}>
          <SelectField
            label="Why"
            value={reason}
            onChange={e => setReason(e.target.value)}
            error={errors.reason}
          >
            {LOST_REASONS.map(r => (
              <option key={r} value={r}>
                {LOST_REASON_LABELS[r]}
              </option>
            ))}
          </SelectField>
          <span className={cn(ui.row, 'mb-4')}>
            <button
              type="button"
              className={cn(ui.btnPrimary, ui.btnSm)}
              disabled={busy}
              onClick={() => run('lose', { reason })}
            >
              Save as lost
            </button>
            <button
              type="button"
              className={cn(ui.btnGhost, ui.btnSm)}
              onClick={() => setMode(null)}
            >
              Cancel
            </button>
          </span>
        </div>
      )}
    </li>
  );
}

export default function VendorPipeline({ vendor, listing, orgName }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setData(await marketApi.listVendorLeads(vendor.vendorId));
    } catch (err) {
      setError(err);
    }
  }, [vendor.vendorId]);

  useEffect(() => {
    load();
  }, [load, listing.listingId]);

  function download() {
    const blob = new Blob([leadsToCsv(data.leads)], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads-${vendor.businessName.replace(/[^A-Za-z0-9]+/g, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (error) return <ErrorAlert error={error} />;
  if (!data) return <Spinner label="Loading your pipeline..." />;
  const { summary } = data;
  return (
    <section className={cn(ui.card, 'mb-6')} aria-labelledby="pipeline-heading">
      <div className={cn(ui.row, ui.spread)}>
        <h2 id="pipeline-heading" className="m-0 text-title-m">
          Pipeline in {orgName}
        </h2>
        {data.leads.length > 0 && (
          <button type="button" className={cn(ui.btnGhost, ui.btnSm)} onClick={download}>
            Download leads (CSV)
          </button>
        )}
      </div>
      <dl className="my-4 grid grid-cols-2 gap-3 md:grid-cols-5">
        {[
          ['Leads', summary.total],
          ['New', summary.counts.new],
          ['Qualified', summary.counts.qualified],
          ['Won', summary.counts.won],
          ['Win rate', summary.winRate === null ? '-' : `${summary.winRate}%`],
        ].map(([label, n]) => (
          <div key={label} className={ui.cardFlat}>
            <dt className={ui.hint}>{label}</dt>
            <dd className="m-0 text-title-m">{n}</dd>
          </div>
        ))}
      </dl>
      <SalespeopleEditor listingId={listing.listingId} initial={data.salespeople} onSaved={load} />
      <RewardProgramEditor listing={listing} />
      {data.leads.length === 0 ? (
        <p className={ui.muted}>No one has joined your interest list here yet.</p>
      ) : (
        <ul className="m-0 list-none p-0" aria-label="Leads">
          {data.leads.map(l => (
            <LeadRow
              key={`${l.leadId}-${l.updatedAt}`}
              lead={l}
              salespeople={data.salespeople}
              onChanged={load}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
